'use client';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H1 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { text0, text4 } from '@osmosis/tailwind-ui/styles/text';
import { CitationData } from '@osmosis/shared/types/pages/answerPages';
import OsmosisAIButton from '../ai/OsmosisAIButton';

type Props = {
  heading: string;
  subheading?: string;
  citations?: CitationData[];
  modified?: string;
};

// Citation types shown on the first line; all other roles wrap to a second line.
const PRIMARY_CITATION_TYPES = ['author', 'editor'];

/**
 * Renders a single line of citations (e.g. "Author: … • Editor: …").
 */
const CitationRow = ({ citations }: { citations: CitationData[] }) => (
  <Flex className={`flex-wrap items-center gap-x-2 text-sm ${text4}`}>
    {citations.map((citation, index) => (
      <Box key={`${citation.type}-${citation.name}`}>
        <span className="font-bold">{citation.type}</span>
        <FormattedMessage id="citation.Colon" defaultMessage=": " />
        <span className="italic">{citation.name}</span>
        {index < citations.length - 1 ? (
          <span className="ml-2">
            <FormattedMessage
              id="answers.header.citationSeparator"
              defaultMessage="•"
            />
          </span>
        ) : null}
      </Box>
    ))}
  </Flex>
);

/**
 * Answer page header: inline, left-aligned title (heading · subheading) with
 * published date and author/editor citation metadata, plus the Ask Osmosis AI
 * entry point on the right.
 */
const AnswerHeader = ({
  heading,
  subheading,
  citations = [],
  modified,
}: Props) => {
  const publishedDate = modified
    ? new Date(modified).toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '';

  // Citations come from untyped composer JSON; `type` can be missing.
  const primaryCitations = citations.filter((citation) =>
    PRIMARY_CITATION_TYPES.includes(citation.type?.toLowerCase()),
  );
  const secondaryCitations = citations.filter(
    (citation) =>
      !PRIMARY_CITATION_TYPES.includes(citation.type?.toLowerCase()),
  );

  return (
    <Flex
      className="w-full flex-col gap-3 pl-6 pt-8 pb-3 md:flex-row md:items-end md:justify-between"
      data-testid="answer-header"
    >
      <Box className="flex flex-col gap-2">
        <H1
          data-testid="answer-header-title"
          className="!text-xl font-bold lg:!text-2xl xl:!text-3xl"
        >
          {heading}
          {subheading ? (
            <span className="font-normal">
              <FormattedMessage
                id="answers.header.titleSeparator"
                defaultMessage=" · "
              />
              {subheading}
            </span>
          ) : null}
        </H1>

        {publishedDate ? (
          <Box
            className={`text-sm ${text4}`}
            data-testid="answer-header-published"
          >
            <span className="font-bold">
              <FormattedMessage
                id="answers.published.Label"
                defaultMessage="Published: "
              />
            </span>
            {publishedDate}
          </Box>
        ) : null}

        {citations.length > 0 ? (
          <Box
            className="flex flex-col gap-1"
            data-testid="answer-header-citations"
          >
            {primaryCitations.length > 0 ? (
              <CitationRow citations={primaryCitations} />
            ) : null}
            {secondaryCitations.length > 0 ? (
              <CitationRow citations={secondaryCitations} />
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Box className="shrink-0">
        <OsmosisAIButton
          leftIcon={
            <Icon icon={faSparkles} className={`mr-1 ${text0}`} size={14} />
          }
          classNames={{
            container: 'w-48',
            button: 'border-none [&>p]:!text-sm',
          }}
          // Answer pages are publicly accessible — always show to logged-out users as a signup driver
          showLoggedOut
        />
      </Box>
    </Flex>
  );
};

export default AnswerHeader;
